import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "../components/Layout";
import api from "../lib/api";
import { MagnifyingGlass, Clock, ArrowRight, Sparkle } from "@phosphor-icons/react";

// Deterministic tint per trade for the cover art
const TRADE_TINTS = [
  { g1: "#11523B", g2: "#0d3f2d", accent: "#E26D29" },
  { g1: "#1E4D4D", g2: "#0f3535", accent: "#F5A365" },
  { g1: "#3B2C1E", g2: "#28190f", accent: "#E26D29" },
  { g1: "#2B3F1E", g2: "#182610", accent: "#F5A365" },
  { g1: "#3B1E33", g2: "#26102b", accent: "#E26D29" },
];
const tintFor = (str) => {
  const s = String(str || "");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return TRADE_TINTS[Math.abs(h) % TRADE_TINTS.length];
};

const Cover = ({ trade, city, small }) => {
  const t = tintFor(trade + city);
  return (
    <div
      className={small ? "rounded-t-lg h-32" : "rounded-t-xl h-44"}
      style={{
        background: `linear-gradient(135deg, ${t.g1} 0%, ${t.g2} 100%)`,
        position: "relative", overflow: "hidden",
      }}
    >
      <div
        style={{
          position: "absolute", top: -30, right: -30, width: 120, height: 120, borderRadius: "50%",
          background: `radial-gradient(circle, ${t.accent}33, transparent 70%)`,
        }}
      />
      <div className="absolute bottom-3 left-4 right-4">
        <div className="text-[10px] font-bold tracking-widest uppercase" style={{ color: t.accent }}>{trade}</div>
        <div className="font-display font-black text-white text-xl leading-none mt-1 truncate">{city}</div>
      </div>
    </div>
  );
};

export default function BlogIndex() {
  const [params, setParams] = useSearchParams();
  const [posts, setPosts] = useState([]);
  const [facets, setFacets] = useState({ trades: [], cities: [] });
  const [q, setQ] = useState(params.get("q") || "");
  const [trade, setTrade] = useState(params.get("trade") || "");
  const [city, setCity] = useState(params.get("city") || "");
  const [loading, setLoading] = useState(true);

  // SEO — set page title and description
  useEffect(() => {
    const suffix = trade || city ? ` · ${[trade, city].filter(Boolean).join(" in ")}` : "";
    document.title = `MyHandyHub Blog${suffix} — Trades tips & UK price guides`;
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", "Practical UK guides on hiring tradespeople — price guides, how-to-find articles, DIY vs pro. Written by the founder of MyHandyHub.");
  }, [trade, city]);

  useEffect(() => {
    (async () => {
      try {
        const [f] = await Promise.all([api.get("/blog/facets")]);
        setFacets(f.data);
      } catch (_) { /* empty */ }
    })();
  }, []);

  useEffect(() => {
    let cancel = false;
    (async () => {
      setLoading(true);
      const query = new URLSearchParams();
      if (q) query.set("q", q);
      if (trade) query.set("trade", trade);
      if (city) query.set("city", city);
      query.set("per_page", "24");
      try {
        const { data } = await api.get(`/blog?${query.toString()}`);
        if (!cancel) setPosts(data.items || []);
      } catch (_) {
        if (!cancel) setPosts([]);
      } finally { if (!cancel) setLoading(false); }
    })();
    return () => { cancel = true; };
  }, [q, trade, city]);

  const updateFilters = (patch) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) next.set(k, v); else next.delete(k);
    }
    setParams(next, { replace: true });
  };

  const [hero, ...rest] = posts;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-5 py-10">
        {/* Header */}
        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[var(--brand-orange)]">
          <Sparkle weight="fill" size={14} /> The MyHandyHub Blog
        </div>
        <h1 className="font-display font-extrabold text-4xl sm:text-5xl mt-2" style={{ color: "var(--brand-green)" }} data-testid="blog-heading">
          UK trades, done right.
        </h1>
        <p className="text-[var(--text-muted)] mt-2 max-w-2xl">
          Straight-talking guides from a homeowner&apos;s perspective — how much things cost, how to spot a bad quote, and when to call in a pro. Written by Adeel, founder of MyHandyHub.
        </p>

        {/* Filters */}
        <div className="mt-6 flex flex-wrap gap-3 items-center">
          <div className="flex-1 min-w-[240px] relative">
            <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" size={18} />
            <input
              value={q}
              onChange={(e) => { setQ(e.target.value); updateFilters({ q: e.target.value }); }}
              placeholder="Search plumber, electrician, cost…"
              data-testid="blog-search"
              className="w-full pl-10 pr-4 py-2.5 rounded-full bg-white border border-[var(--border)] focus:outline-none focus:ring-2 focus:ring-[var(--brand-orange)]"
            />
          </div>
          <select
            value={trade}
            onChange={(e) => { setTrade(e.target.value); updateFilters({ trade: e.target.value }); }}
            className="px-4 py-2.5 rounded-full bg-white border border-[var(--border)] text-sm font-semibold text-[var(--brand-green)]"
            data-testid="blog-trade-filter"
          >
            <option value="">All trades</option>
            {facets.trades.map((t) => <option key={t} value={t}>{t}</option>)}
          </select>
          <select
            value={city}
            onChange={(e) => { setCity(e.target.value); updateFilters({ city: e.target.value }); }}
            className="px-4 py-2.5 rounded-full bg-white border border-[var(--border)] text-sm font-semibold text-[var(--brand-green)]"
            data-testid="blog-city-filter"
          >
            <option value="">All cities</option>
            {facets.cities.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {loading && <p className="text-[var(--text-muted)] mt-8">Loading articles…</p>}
        {!loading && posts.length === 0 && (
          <p className="text-[var(--text-muted)] mt-8" data-testid="blog-empty">No articles matched your filters yet. Try clearing them.</p>
        )}

        {/* Hero article */}
        {hero && (
          <Link
            to={`/blog/${hero.slug}`}
            className="block mt-8 brand-card overflow-hidden hover:shadow-lg transition"
            data-testid={`blog-hero-${hero.slug}`}
          >
            <div className="grid md:grid-cols-2 gap-0">
              <Cover trade={hero.trade} city={hero.city} />
              <div className="p-5 md:p-6">
                <div className="text-xs font-bold uppercase tracking-widest text-[var(--brand-orange)]">Featured</div>
                <h2 className="font-display font-extrabold text-2xl mt-1" style={{ color: "var(--brand-green)" }}>{hero.title}</h2>
                <p className="text-[var(--text-muted)] mt-2 line-clamp-3">{hero.excerpt}</p>
                <div className="flex items-center gap-3 mt-4 text-xs text-[var(--text-muted)]">
                  <span className="inline-flex items-center gap-1"><Clock size={13} /> {hero.read_minutes} min read</span>
                  <span>·</span>
                  <span className="font-semibold text-[var(--brand-green)]">Read article</span>
                  <ArrowRight size={13} className="text-[var(--brand-orange)]" />
                </div>
              </div>
            </div>
          </Link>
        )}

        {/* Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-5 mt-6">
          {rest.map((p) => (
            <Link
              to={`/blog/${p.slug}`}
              key={p.slug}
              className="brand-card overflow-hidden hover:shadow-lg transition"
              data-testid={`blog-card-${p.slug}`}
            >
              <Cover trade={p.trade} city={p.city} small />
              <div className="p-4">
                <div className="text-[10px] font-bold uppercase tracking-widest text-[var(--brand-orange)]">{p.trade} · {p.city}</div>
                <h3 className="font-display font-extrabold text-lg mt-1 line-clamp-2" style={{ color: "var(--brand-green)" }}>{p.title}</h3>
                <p className="text-sm text-[var(--text-muted)] mt-1.5 line-clamp-2">{p.excerpt}</p>
                <div className="flex items-center gap-1 text-xs text-[var(--text-muted)] mt-3">
                  <Clock size={12} /> {p.read_minutes} min read
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </Layout>
  );
}
